import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import Icon from "@mdi/react";
import { mdiAndroid, mdiApple, mdiArrowLeft } from "@mdi/js";
import {
  Button,
  Input,
  ListBox,
  NumberField,
  Radio,
  RadioGroup,
  SearchField,
  Select,
  TextArea,
  TextField,
  Modal,
} from "@heroui/react";
import { ROUTES } from "@/constants/routes";
import { pushCampaignsStore } from "@/stores/PushCampaignsStore";
import { LINK_KIND_LABELS } from "@/stores/push-campaigns/pushCampaignLabels";
import {
  PUSH_TEXT_MAX_BYTES,
  pushTextByteLength,
} from "@/stores/push-campaigns/PushCampaignFormDto";
import "./PushCampaignEditorPage.css";

function formatClientPhone(phone: string | null | undefined): string {
  const value = phone?.trim();
  return value ? value : "—";
}

const LINK_KIND_OPTIONS = [
  { value: "none", label: LINK_KIND_LABELS.none },
  { value: "service_center", label: LINK_KIND_LABELS.service_center },
  { value: "closest", label: LINK_KIND_LABELS.closest },
  { value: "bookings", label: LINK_KIND_LABELS.bookings },
];

const BUSINESS_TYPE_OPTIONS = [
  { value: "any", label: "Любой" },
  { value: "car_wash", label: "Мойка" },
  { value: "tire_service", label: "Шиномонтаж" },
] as const;

type PreviewPlatform = "ios" | "android";

const PushCampaignEditorPage = observer(() => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const store = pushCampaignsStore;
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [previewPlatform, setPreviewPlatform] = useState<PreviewPlatform>("ios");
  const isEdit = Boolean(uuid);
  const canSend = !store.isEditorLocked(uuid);

  useEffect(() => {
    if (uuid) {
      void store.loadEditor(uuid);
    } else {
      void store.loadEditor();
    }
    void store.loadPushableClients();
    void store.loadServiceCenters();
    return () => {
      store.disposeEditor();
    };
  }, [uuid, store]);

  const backPath = isEdit
    ? ROUTES.PUSH_CAMPAIGN_DETAIL.replace(":uuid", uuid ?? "")
    : ROUTES.PUSH_CAMPAIGNS;

  const preview = store.audiencePreview;
  const testClientsById = new Map<string, (typeof store.pushableClients)[number]>();
  for (const client of store.audienceClientCards) {
    if (client.phone?.trim()) testClientsById.set(client.uuid, client);
  }
  for (const client of store.pushableClients) {
    if (client.phone?.trim() && !testClientsById.has(client.uuid)) {
      testClientsById.set(client.uuid, client);
    }
  }
  const testClientsWithPhone = [...testClientsById.values()];

  const saveDraft = async () => {
    const id = await store.saveDraft();
    if (id && !uuid) {
      navigate(ROUTES.PUSH_CAMPAIGN_EDIT.replace(":uuid", id), {
        replace: true,
      });
    }
  };

  const serviceCenterChips = useMemo(() => {
    const options = [...store.serviceCenterOptions];
    const selectedId = store.form.linkServiceCenterUuid;
    if (
      selectedId &&
      !options.some((item) => item.uuid === selectedId)
    ) {
      options.unshift({
        uuid: selectedId,
        name: store.form.linkServiceCenterName || "Выбранный сервис",
      });
    }
    return options;
  }, [
    store.serviceCenterOptions,
    store.form.linkServiceCenterUuid,
    store.form.linkServiceCenterName,
  ]);

  return (
    <>
      <div className="pce-page" data-testid="push-campaign-editor">
        <header className="pce-header">
          <button
            type="button"
            className="pce-back"
            aria-label={isEdit ? "К карточке рассылки" : "К списку рассылок"}
            data-testid="push-campaign-editor-back-button"
            onClick={() => navigate(backPath)}
          >
            <Icon path={mdiArrowLeft} size={0.85} />
          </button>
          <div className="pce-header__text">
            <h1 className="pce-header__title">
              {isEdit ? "Редактирование рассылки" : "Новая рассылка"}
            </h1>
            <p className="pce-header__hint">
              Время планирования вводится в часовом поясе браузера и проверяется по Москве.
            </p>
          </div>
          <div className="pce-header__actions">
            <Button
              variant="primary"
              isPending={store.isSaving}
              data-testid="push-campaign-editor-save-button"
              onPress={() => void saveDraft()}
            >
              Сохранить
            </Button>
          </div>
        </header>

        <div className="pce-layout">
          <div className="pce-main">
            <section className="pce-card">
              <h2 className="pce-card__title">Сообщение</h2>
              <fieldset className="pce-form-fieldset">
                <label className="pce-field" data-testid="push-campaign-name-field">
                  <span className="pce-field__label">Внутреннее название</span>
                  <TextField
                    fullWidth
                    isInvalid={Boolean(store.validationErrors.name)}
                  >
                    <Input
                      data-testid="push-campaign-name-input"
                      value={store.form.name}
                      onChange={(e) => {
                        store.form.name = e.target.value;
                      }}
                    />
                  </TextField>
                  {store.validationErrors.name && (
                    <span className="pce-error">{store.validationErrors.name}</span>
                  )}
                </label>

                <label className="pce-field" data-testid="push-campaign-title-field">
                  <span className="pce-field__label">Заголовок</span>
                  <TextField
                    fullWidth
                    isInvalid={Boolean(store.validationErrors.title)}
                  >
                    <Input
                      data-testid="push-campaign-title-input"
                      value={store.form.title}
                      onChange={(e) => {
                        store.form.title = e.target.value;
                      }}
                    />
                  </TextField>
                  {store.validationErrors.title && (
                    <span className="pce-error">{store.validationErrors.title}</span>
                  )}
                </label>

                <label className="pce-field">
                  <span className="pce-field__label">Подзаголовок</span>
                  <TextField fullWidth>
                    <Input
                      value={store.form.subtitle}
                      onChange={(e) => {
                        store.form.subtitle = e.target.value;
                      }}
                    />
                  </TextField>
                </label>

                <label className="pce-field" data-testid="push-campaign-body-field">
                  <span className="pce-field__label">Текст</span>
                  <TextArea
                    fullWidth
                    rows={4}
                    data-testid="push-campaign-body-input"
                    value={store.form.body}
                    onChange={(e) => {
                      store.form.body = e.target.value;
                    }}
                  />
                  {store.validationErrors.body && (
                    <span className="pce-error">{store.validationErrors.body}</span>
                  )}
                  <span className="pce-hint">
                    {pushTextByteLength(store.form)} / {PUSH_TEXT_MAX_BYTES} байт.
                    Длинный текст система обрежет на экране блокировки.
                  </span>
                </label>

                <label className="pce-field">
                  <span className="pce-field__label">Действие при нажатии</span>
                  <Select
                    aria-label="Действие при нажатии"
                    selectedKey={store.form.linkKind}
                    onSelectionChange={(key) => {
                      if (!key) return;
                      store.form.linkKind = String(
                        key,
                      ) as typeof store.form.linkKind;
                    }}
                  >
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        {LINK_KIND_OPTIONS.map((opt) => (
                          <ListBox.Item
                            key={opt.value}
                            id={opt.value}
                            textValue={opt.label}
                          >
                            {opt.label}
                          </ListBox.Item>
                        ))}
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </label>

                {store.form.linkKind === "service_center" && (
                  <div className="pce-field">
                    <span className="pce-field__label">Сервисный центр</span>
                    <SearchField
                      value={store.serviceCenterSearch}
                      onChange={(v) => {
                        store.serviceCenterSearch = v;
                        void store.loadServiceCenters();
                      }}
                      aria-label="Название сервиса"
                    >
                      <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input placeholder="Название сервиса" />
                        <SearchField.ClearButton />
                      </SearchField.Group>
                    </SearchField>
                    <div className="pce-chips" role="listbox" aria-label="Сервисный центр">
                      {serviceCenterChips.length === 0 ? (
                        <span className="pce-hint">
                          {store.isServiceCentersLoading
                            ? "Загрузка…"
                            : "Сервисы не найдены"}
                        </span>
                      ) : (
                        serviceCenterChips.map((sc) => {
                          const selected =
                            store.form.linkServiceCenterUuid === sc.uuid;
                          return (
                            <button
                              key={sc.uuid}
                              type="button"
                              role="option"
                              aria-selected={selected}
                              className={
                                selected
                                  ? "pce-chip pce-chip--selected"
                                  : "pce-chip"
                              }
                              onClick={() => {
                                store.form.linkServiceCenterUuid = sc.uuid;
                                store.form.linkServiceCenterName = sc.name;
                              }}
                            >
                              {sc.name}
                            </button>
                          );
                        })
                      )}
                    </div>
                    {store.validationErrors.linkServiceCenterUuid && (
                      <span className="pce-error">
                        {store.validationErrors.linkServiceCenterUuid}
                      </span>
                    )}
                  </div>
                )}
              </fieldset>
            </section>

            <section className="pce-card">
              <h2 className="pce-card__title">Воронка</h2>
              <fieldset className="pce-form-fieldset">
                <div className="pce-field">
                  <span className="pce-field__label">Тип бизнеса</span>
                  <RadioGroup
                    aria-label="Тип бизнеса"
                    value={store.form.targetBusinessType ?? "any"}
                    onChange={(value) => {
                      const v = String(value);
                      store.form.targetBusinessType =
                        v === "any" ? null : (v as "car_wash" | "tire_service");
                      store.scheduleAudiencePreview();
                    }}
                    className="pce-radios"
                  >
                    {BUSINESS_TYPE_OPTIONS.map((opt) => (
                      <Radio key={opt.value} value={opt.value}>
                        <Radio.Control>
                          <Radio.Indicator />
                        </Radio.Control>
                        <Radio.Content>{opt.label}</Radio.Content>
                      </Radio>
                    ))}
                  </RadioGroup>
                </div>

                <label className="pce-field pce-attribution-field">
                  <span className="pce-field__label">Окно атрибуции (часы)</span>
                  <NumberField
                    value={store.form.attributionWindowHours}
                    minValue={1}
                    maxValue={168}
                    onChange={(v) => {
                      store.form.attributionWindowHours = v ?? 72;
                    }}
                  >
                    <NumberField.Group className="pce-attribution-field__group">
                      <NumberField.Input aria-label="Окно атрибуции в часах" />
                    </NumberField.Group>
                  </NumberField>
                </label>

                <label className="pce-field">
                  <span className="pce-field__label">Аудитория</span>
                  <Select
                    aria-label="Аудитория"
                    selectedKey={store.form.audienceMode}
                    onSelectionChange={(key) => {
                      if (!key) return;
                      store.form.audienceMode = String(
                        key,
                      ) as typeof store.form.audienceMode;
                      store.scheduleAudiencePreview();
                    }}
                  >
                    <Select.Trigger>
                      <Select.Value />
                      <Select.Indicator />
                    </Select.Trigger>
                    <Select.Popover>
                      <ListBox>
                        <ListBox.Item id="all_matching" textValue="Все подходящие">
                          Все подходящие
                        </ListBox.Item>
                        <ListBox.Item id="client_uuids" textValue="Выбранные клиенты">
                          Выбранные клиенты
                        </ListBox.Item>
                      </ListBox>
                    </Select.Popover>
                  </Select>
                </label>

                {store.form.audienceMode === "all_matching" ? (
                  <SearchField
                    value={store.form.audienceQuery}
                    onChange={(v) => {
                      store.form.audienceQuery = v;
                      store.scheduleAudiencePreview();
                    }}
                    aria-label="Фильтр аудитории"
                  >
                    <SearchField.Group>
                      <SearchField.SearchIcon />
                      <SearchField.Input placeholder="Фильтр аудитории" />
                      <SearchField.ClearButton />
                    </SearchField.Group>
                  </SearchField>
                ) : (
                  <div className="pce-field">
                    <SearchField
                      value={store.pushableSearch}
                      onChange={(v) => {
                        store.pushableSearch = v;
                        void store.loadPushableClients();
                      }}
                      aria-label="Поиск клиентов"
                    >
                      <SearchField.Group>
                        <SearchField.SearchIcon />
                        <SearchField.Input placeholder="Поиск клиентов" />
                        <SearchField.ClearButton />
                      </SearchField.Group>
                    </SearchField>
                    <div className="pce-clients">
                      {store.audiencePickerClients.map((client) => (
                        <label key={client.uuid} className="pce-client-checkbox">
                          <input
                            type="checkbox"
                            className="pce-native-check"
                            checked={store.form.selectedClientUuids.includes(
                              client.uuid,
                            )}
                            onChange={() => store.toggleSelectedClient(client.uuid)}
                          />
                          <span>
                            {formatClientPhone(client.phone) !== "—"
                              ? formatClientPhone(client.phone)
                              : (client.name ?? client.uuid)}
                          </span>
                        </label>
                      ))}
                    </div>
                    {store.validationErrors.client_uuids && (
                      <span className="pce-error">
                        {store.validationErrors.client_uuids}
                      </span>
                    )}
                  </div>
                )}

                <p className="pce-hint">
                  Аудитория:{" "}
                  {store.isAudiencePreviewLoading
                    ? "…"
                    : `${preview?.clientsCount ?? 0} клиентов, ${preview?.devicesCount ?? 0} устройств`}
                </p>
              </fieldset>
            </section>

            {canSend && (
              <section className="pce-card">
                <h2 className="pce-card__title">Тест</h2>
                <p className="pce-hint">До 5 клиентов с телефоном</p>
                <div className="pce-clients">
                  {testClientsWithPhone.length === 0 ? (
                    <p className="pce-hint">Нет клиентов с телефоном</p>
                  ) : (
                    testClientsWithPhone.map((client) => (
                      <label key={`test-${client.uuid}`} className="pce-client-checkbox">
                        <input
                          type="checkbox"
                          className="pce-native-check"
                          checked={store.testSelectedUuids.includes(client.uuid)}
                          onChange={() => store.toggleTestClient(client.uuid)}
                        />
                        <span>{formatClientPhone(client.phone)}</span>
                      </label>
                    ))
                  )}
                </div>
                {store.validationErrors.test && (
                  <span className="pce-error">{store.validationErrors.test}</span>
                )}
                <div>
                  <Button
                    variant="outline"
                    data-testid="push-campaign-send-test-button"
                    onPress={() => void store.sendTest()}
                  >
                    Отправить тест
                  </Button>
                </div>

                <h2 className="pce-card__title pce-card__title--split">
                  Запланировать
                </h2>
                <div className="pce-schedule">
                  <label className="pce-field">
                    <span className="pce-field__label">Дата</span>
                    <TextField>
                      <Input
                        type="date"
                        value={store.scheduleDate}
                        onChange={(e) => {
                          store.scheduleDate = e.target.value;
                        }}
                      />
                    </TextField>
                  </label>
                  <label className="pce-field">
                    <span className="pce-field__label">Время</span>
                    <TextField>
                      <Input
                        type="time"
                        value={store.scheduleTime}
                        onChange={(e) => {
                          store.scheduleTime = e.target.value;
                        }}
                      />
                    </TextField>
                  </label>
                </div>
                {store.validationErrors.schedule && (
                  <span className="pce-error">{store.validationErrors.schedule}</span>
                )}
                <div>
                  <Button
                    data-testid="push-campaign-schedule-button"
                    onPress={() => void store.scheduleCampaign()}
                  >
                    Запланировать
                  </Button>
                </div>
              </section>
            )}

            {canSend && (
              <div className="pce-actions">
                <Button
                  variant="primary"
                  data-testid="push-campaign-send-now-button"
                  onPress={() => setConfirmSendOpen(true)}
                >
                  Отправить сейчас
                </Button>
              </div>
            )}
          </div>

          <aside className="pce-card pce-preview-card">
            <div className="pce-platform" role="tablist" aria-label="Платформа превью">
              <button
                type="button"
                role="tab"
                aria-selected={previewPlatform === "ios"}
                className={
                  previewPlatform === "ios"
                    ? "pce-platform__btn pce-platform__btn--active"
                    : "pce-platform__btn"
                }
                onClick={() => setPreviewPlatform("ios")}
              >
                <Icon path={mdiApple} size={0.7} />
                iOS
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={previewPlatform === "android"}
                className={
                  previewPlatform === "android"
                    ? "pce-platform__btn pce-platform__btn--active"
                    : "pce-platform__btn"
                }
                onClick={() => setPreviewPlatform("android")}
              >
                <Icon path={mdiAndroid} size={0.7} />
                Android
              </button>
            </div>

            <div
              className={
                previewPlatform === "android"
                  ? "pce-note pce-note--android"
                  : "pce-note"
              }
            >
              <div className="pce-note__app">PRO STO</div>
              <div className="pce-note__title">
                {store.form.title || "Заголовок"}
              </div>
              {store.form.subtitle ? (
                <div className="pce-note__subtitle">{store.form.subtitle}</div>
              ) : null}
              <div className="pce-note__body">
                {store.form.body || "Текст уведомления"}
              </div>
            </div>
            <p className="pce-hint">
              Превью показывает, как уведомление читается до обрезки системой.
            </p>
          </aside>
        </div>
      </div>

      <Modal.Backdrop isOpen={confirmSendOpen} onOpenChange={setConfirmSendOpen}>
        <Modal.Container>
          <Modal.Dialog data-testid="push-campaign-send-confirm-modal">
            <Modal.Header>
              <Modal.Heading>Подтвердите отправку</Modal.Heading>
            </Modal.Header>
            <Modal.Body>
              Будет отправлено на {preview?.clientsCount ?? 0} клиентов (
              {preview?.devicesCount ?? 0} устройств). Продолжить?
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="ghost"
                data-testid="push-campaign-send-confirm-cancel"
                onPress={() => setConfirmSendOpen(false)}
              >
                Отмена
              </Button>
              <Button
                variant="primary"
                data-testid="push-campaign-send-confirm-submit"
                onPress={async () => {
                  const ok = await store.sendNow();
                  setConfirmSendOpen(false);
                  if (ok && store.editingUuid) {
                    navigate(
                      ROUTES.PUSH_CAMPAIGN_DETAIL.replace(
                        ":uuid",
                        store.editingUuid,
                      ),
                    );
                  }
                }}
              >
                Отправить
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </>
  );
});

PushCampaignEditorPage.displayName = "PushCampaignEditorPage";
export default PushCampaignEditorPage;
